/** Client-side contract pricing — mirrors backend contract-generation-v3/estimating.ts */

export interface EstimatingParams {
    paymentType: string;
    wasteFactorPercent: number;
    steepRate7_9: number;
    steepRate10_12: number;
    steepRate13Plus: number;
    tearOffLayers: number;
    extraLayerRatePerSq: number;
    stories: number;
    deductible: number;
    opPercent: number;
    salesTaxRate: number;
    overheadPercent: number;
    targetMarginPercent: number;
    salesmanCommissionPercent: number;
    ccFeePercent: number;
    permitCost: number;
    dumpsterFee: number;
    eagleviewCost: number;
    referralFee: number;
    tripCharge: number;
    noAccessFee: number;
    jobPhotosFee: number;
    bidAmountOverride: number | null;
    billedAmount: number | null;
    shingleUpgradeNotes: string;
    shingleUpgradeAmount: number;
    paymentTerms: string;
}

const num = (val: string | undefined, fallback: number): number => {
    if (val === undefined || val === null || val === "") return fallback;
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
};

function getAttrValue(attributes: any[], name: string): string {
    return attributes.find((a) => a.AttributeName === name)?.AttributeValue ?? "";
}

function parseTearOffLayers(attributes: any[]): number {
    const val = getAttrValue(attributes, "Tear Off Layer");
    if (!val) return 1;
    const lower = val.toLowerCase();
    if (lower.includes("3rd") || lower.includes("3+") || lower.includes("3 layer")) return 3;
    if (lower.includes("2nd") || lower.includes("2 layer") || /\b2\b/.test(val)) return 2;
    const n = parseInt(val.replace(/\D/g, ""), 10);
    if (!isNaN(n) && n > 0) return n;
    return 1;
}

function parseStories(attributes: any[]): number {
    const val = getAttrValue(attributes, "Stories");
    const n = parseInt(val, 10);
    return !isNaN(n) && n > 0 ? n : 1;
}

export function parseEstimatingAttributes(attributes: any[]): EstimatingParams {
    const map = new Map<string, string>();
    attributes.forEach((a) => {
        if (a.AttributeName?.startsWith("EST_")) {
            map.set(a.AttributeName, a.AttributeValue ?? "");
        }
    });

    const bidRaw = map.get("EST_BID_AMOUNT");
    const billedRaw = map.get("EST_BILLED_AMOUNT");

    return {
        paymentType: map.get("EST_PAYMENT_TYPE") || "insurance",
        wasteFactorPercent: num(map.get("EST_WASTE_FACTOR"), 10),
        steepRate7_9: num(map.get("EST_STEEP_RATE_7_9"), 25),
        steepRate10_12: num(map.get("EST_STEEP_RATE_10_12"), 45),
        steepRate13Plus: num(map.get("EST_STEEP_RATE_13_PLUS"), 75),
        tearOffLayers: parseTearOffLayers(attributes),
        extraLayerRatePerSq: num(map.get("EST_EXTRA_LAYER_RATE"), 15),
        stories: parseStories(attributes),
        deductible: num(map.get("EST_DEDUCTIBLE"), 0),
        opPercent: num(map.get("EST_OP_PERCENT"), 20),
        salesTaxRate: num(map.get("EST_SALES_TAX_RATE"), 7.5) / 100,
        overheadPercent: num(map.get("EST_OVERHEAD_PERCENT"), 3.25) / 100,
        targetMarginPercent: num(map.get("EST_TARGET_MARGIN"), 25) / 100,
        salesmanCommissionPercent: num(map.get("EST_SALESMAN_COMMISSION_PERCENT"), 30) / 100,
        ccFeePercent: num(map.get("EST_CC_FEE_PERCENT"), 3),
        permitCost: num(map.get("EST_PERMIT_COST"), 0),
        dumpsterFee: num(map.get("EST_DUMPSTER_FEE"), 0),
        eagleviewCost: num(map.get("EST_EAGLEVIEW_COST"), 0),
        referralFee: num(map.get("EST_REFERRAL_FEE"), 0),
        tripCharge: num(map.get("EST_TRIP_CHARGE"), 0),
        noAccessFee: num(map.get("EST_NO_ACCESS_FEE"), 0),
        jobPhotosFee: num(map.get("EST_JOB_PHOTOS_FEE"), 0),
        bidAmountOverride: bidRaw && bidRaw !== "" ? num(bidRaw, 0) : null,
        billedAmount: billedRaw && billedRaw !== "" ? num(billedRaw, 0) : null,
        shingleUpgradeNotes: map.get("EST_SHINGLE_UPGRADE_NOTES") || "",
        shingleUpgradeAmount: num(map.get("EST_SHINGLE_UPGRADE_AMOUNT"), 0),
        paymentTerms: map.get("EST_PAYMENT_TERMS") || "",
    };
}

export const isGutterItem = (item: any): boolean =>
    item.ProductCategory === "Gutters" ||
    item.ProductCategory === "Downspouts" ||
    item.ProductCategory === "Gutter Screens" ||
    (item.ProductName || "").toLowerCase().includes("gutter") ||
    (item.ProductName || "").toLowerCase().includes("downspout");

export function buildPaymentSchedule(est: EstimatingParams): string {
    if (est.paymentTerms && est.paymentTerms.trim() !== "") {
        return est.paymentTerms;
    }
    if (est.paymentType === "insurance") {
        return `Insurance claim — O&P ${est.opPercent}%${est.deductible > 0 ? `; deductible $${est.deductible.toFixed(2)}` : ""}`;
    }
    if (est.paymentType === "financing") return "Financing per lender agreement";
    if (est.paymentType === "credit") return `Credit card — ${est.ccFeePercent}% processing fee included`;
    if (est.paymentType === "debit") return "Debit card at project completion";
    return "50% at project start; balance due upon completion";
}

export function applyEstimatingToContractPrice(args: {
    est: EstimatingParams;
    roofMaterials: number;
    gutterMaterials: number;
    roofLabor: number;
    gutterLabor: number;
}) {
    const { est, roofMaterials, gutterMaterials, roofLabor, gutterLabor } = args;

    const wasteMarkup = roofMaterials * (est.wasteFactorPercent / 100);
    const roofSubtotal = roofMaterials + wasteMarkup + roofLabor;
    const gutterSubtotal = gutterMaterials + gutterLabor;
    const steepCharge = 0;
    const extraLayerCharge = 0;
    const logisticsFees =
        est.permitCost + est.dumpsterFee + est.eagleviewCost + est.referralFee + est.jobPhotosFee;
    const taxableMaterials = roofMaterials + wasteMarkup + gutterMaterials;
    const salesTax = taxableMaterials * est.salesTaxRate;
    const totalCost = roofSubtotal + gutterSubtotal + steepCharge + extraLayerCharge + logisticsFees + salesTax;

    let sellingPrice: number;
    if (est.bidAmountOverride !== null && est.bidAmountOverride > 0) {
        sellingPrice = est.bidAmountOverride;
    } else {
        const margin = Math.min(Math.max(est.targetMarginPercent, 0), 0.95);
        sellingPrice = totalCost / (1 - margin);
    }

    sellingPrice *= 1 + est.overheadPercent;

    if (est.paymentType === "credit" && est.ccFeePercent > 0) {
        sellingPrice *= 1 + est.ccFeePercent / 100;
    }

    return {
        roofMaterials,
        gutterMaterials,
        roofLabor,
        gutterLabor,
        wasteMarkup,
        steepCharge,
        extraLayerCharge,
        logisticsFees,
        salesTax,
        totalCost,
        sellingPrice,
        paymentSchedule: buildPaymentSchedule(est),
    };
}

export interface ContractPricingLine {
    label: string;
    amount: string;
    isTotal?: boolean;
}

export function buildPricingBreakdownLines(
    breakdown: ReturnType<typeof calculateCartBreakdown>,
    pricing: ReturnType<typeof applyEstimatingToContractPrice>,
    est: EstimatingParams,
    format: (n: number) => string,
): ContractPricingLine[] {
    const cartSubtotal =
        breakdown.roofMaterials +
        breakdown.roofLabor +
        breakdown.gutterMaterials +
        breakdown.gutterLabor;
    const roofScope =
        breakdown.roofMaterials +
        breakdown.roofLabor +
        pricing.wasteMarkup +
        pricing.steepCharge +
        pricing.extraLayerCharge;
    const gutterScope = breakdown.gutterMaterials + breakdown.gutterLabor;

    const lines: ContractPricingLine[] = [
        { label: "Materials & labor subtotal", amount: format(cartSubtotal) },
    ];

    if (pricing.wasteMarkup > 0) {
        lines.push({
            label: `Roof waste allowance (${est.wasteFactorPercent}%)`,
            amount: format(pricing.wasteMarkup),
        });
    }
    if (pricing.steepCharge > 0) {
        lines.push({ label: "Steep pitch adjustment", amount: format(pricing.steepCharge) });
    }
    if (pricing.extraLayerCharge > 0) {
        lines.push({ label: "Additional tear-off layers", amount: format(pricing.extraLayerCharge) });
    }

    lines.push(
        { label: "Roof scope total", amount: format(roofScope) },
        { label: "Gutter scope total", amount: format(gutterScope) },
        { label: "Subtotal — roof & gutter scope", amount: format(roofScope + gutterScope) },
    );

    if (pricing.logisticsFees > 0) {
        lines.push({
            label: "Project fees (permit, dumpster, EagleView, etc.)",
            amount: format(pricing.logisticsFees),
        });
    }
    if (pricing.salesTax > 0) {
        lines.push({
            label: `Sales tax (${(est.salesTaxRate * 100).toFixed(1)}%)`,
            amount: format(pricing.salesTax),
        });
    }

    lines.push({ label: "Subtotal before profit", amount: format(pricing.totalCost) });

    if (est.bidAmountOverride !== null && est.bidAmountOverride > 0) {
        lines.push({
            label: "Manual contract amount (override)",
            amount: format(est.bidAmountOverride),
        });
    } else {
        const beforeOverhead = pricing.sellingPrice / (1 + est.overheadPercent);
        const marginAmount = beforeOverhead - pricing.totalCost;
        lines.push({
            label: `Profit margin (${(est.targetMarginPercent * 100).toFixed(0)}%)`,
            amount: format(marginAmount),
        });
        if (est.overheadPercent > 0) {
            lines.push({
                label: `General overhead (${(est.overheadPercent * 100).toFixed(2)}%)`,
                amount: format(beforeOverhead * est.overheadPercent),
            });
        }
    }

    lines.push({
        label: "CONTRACT TOTAL",
        amount: format(pricing.sellingPrice),
        isTotal: true,
    });

    return lines;
}

export function calculateCartBreakdown(cartItems: any[]) {
    let roofMaterials = 0;
    let gutterMaterials = 0;
    let roofLabor = 0;
    let gutterLabor = 0;

    for (const item of cartItems || []) {
        if (!item.ItemCount || item.ItemCount <= 0) continue;
        const lineTotal = (Number(item.UnitPrice) || 0) * Number(item.ItemCount);
        const isLabor = item.ProductCategory === "Labor" || !!item.LaborServiceId;
        const gutter = isGutterItem(item);

        if (gutter) {
            if (isLabor) gutterLabor += lineTotal;
            else gutterMaterials += lineTotal;
        } else if (isLabor) {
            roofLabor += lineTotal;
        } else {
            roofMaterials += lineTotal;
        }
    }

    return { roofMaterials, gutterMaterials, roofLabor, gutterLabor };
}

export function formatCurrency(n: number): string {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}
